import packedSchema from './schema/graphql.txt';

const isAbsoluteUrl = require('is-absolute-url');
const { pick } = require('./helpers/lite');
const Mcrypto = require('@ocap/mcrypto');
const { fromBase64, toUint8Array } = require('@ocap/util');
const BaseClient = require('@arcblock/sdk-util');
const jsonpack = require('jsonpack');

const SUBSCRIBE_NOT_INSTALLED =
  'subscribe/unsubscribe are not installed in this build. Import "@ocap/client/subscribe" and call install(client) to enable topic subscriptions.';

// Gas-payer signing only applies to `mutation { sendTx(tx: "...") ... }`.
// The legacy path ran a full GraphQL parse (graphql/language/parser ~42 KB)
// just to pick out the literal `tx` arg. The mutation shape is tightly
// constrained: `sdk-util`'s builder emits `mutation{sendTx(tx: "...", ...)}`
// and callers never use `$tx` variables. A bounded regex match reproduces
// the original contract — we degrade to `{}` (no gas-payer header) for any
// non-matching query, matching the prior behavior for non-sendTx mutations.
const SEND_TX_MUTATION_RE = /^\s*mutation\b[^{(]*(?:\([^)]*\))?\s*\{\s*sendTx\s*\(([^)]*)\)/;
const SEND_TX_LITERAL_RE = /\btx\s*:\s*"((?:[^"\\]|\\.)*)"/;

/**
 * Provides a readonly client to forge
 * Its recommended to use this version in browser to get smaller bundle size
 *
 * @class GraphQLClientBase
 * @extends {BaseClient}
 */
class GraphQLClientBase extends BaseClient {
  constructor(endpoint) {
    if (isAbsoluteUrl(endpoint) === false) {
      throw new Error('Chain endpoint must be a valid url');
    }

    super({
      dataSource: 'forge',
      httpEndpoint: endpoint,
      socketEndpoint: () => this._getSocketEndpoint(endpoint),
      enableQuery: true,
      enableSubscription: true,
      enableMutation: true,
      maxQueryDepth: 6,
    });

    this._endpoint = endpoint;
    this._socketClient = null;
  }

  setGasPayer(wallet) {
    this._gasPayer = wallet;
  }

  subscribe() {
    throw new Error(SUBSCRIBE_NOT_INSTALLED);
  }

  unsubscribe() {
    throw new Error(SUBSCRIBE_NOT_INSTALLED);
  }

  _ensureSocketClient() {
    throw new Error(SUBSCRIBE_NOT_INSTALLED);
  }

  _getSocketEndpoint(endpoint) {
    let socketEndpoint = endpoint.replace(/https?:\/\//, 'ws://');
    if (endpoint.indexOf('https://') === 0) {
      socketEndpoint = socketEndpoint.replace('ws://', 'wss://');
    }

    return socketEndpoint;
  }

  async _getAuthHeaders(query) {
    if (!this._gasPayer) {
      return {};
    }
    if (typeof this._gasPayer.sign !== 'function') {
      return {};
    }

    if (typeof query !== 'string') {
      return {};
    }

    const sendTxMatch = query.match(SEND_TX_MUTATION_RE);
    if (!sendTxMatch) {
      return {};
    }

    const txMatch = sendTxMatch[1].match(SEND_TX_LITERAL_RE);
    if (!txMatch) {
      return {};
    }

    const txBuffer = toUint8Array(fromBase64(txMatch[1]));
    const txHash = Mcrypto.toTxHash(txBuffer);
    return {
      'x-gas-payer-pk': this._gasPayer.publicKey,
      'x-gas-payer-sig': await this._gasPayer.signJWT({ txHash }),
    };
  }

  _getSchema() {
    return jsonpack.unpack(packedSchema);
  }

  _getIgnoreFields() {
    return [];
  }

  pickGasPayerHeaders({ headers }) {
    return pick(headers, ['x-gas-payer-sig', 'x-gas-payer-pk']);
  }
}

module.exports = GraphQLClientBase;
